import { isInternalStaffEmail } from "./isInternalStaffEmail";

export type PlanKey = "starter" | "pro" | "growth";

export const PLAN_LABELS: Record<PlanKey, string> = {
  starter: "Starter",
  pro: "Pro",
  growth: "Growth",
};

const PLAN_AMOUNTS: Record<PlanKey, number[]> = {
  starter: [15, 19, 27],
  pro: [39, 47, 49],
  growth: [79, 97, 99],
};

const TIER_RANK: Record<PlanKey, number> = {
  starter: 1,
  pro: 2,
  growth: 3,
};

function roundAmount(amount: unknown): number {
  if (amount == null || amount === "") return 0;
  if (typeof amount === "number") return Math.round(amount);
  return Math.round(Number(String(amount).replace(/[^0-9.]/g, "")) || 0);
}

export function planFromAmount(amount: unknown): PlanKey | "" {
  const n = roundAmount(amount);
  if (!n) return "";
  for (const key of Object.keys(PLAN_AMOUNTS) as PlanKey[]) {
    if (PLAN_AMOUNTS[key].includes(n)) return key;
  }
  return "";
}

export function namedPlanKey(value: unknown): PlanKey | "" {
  const s = String(value || "").trim().toLowerCase();
  if (!s || s.startsWith("unnamed") || s.startsWith("unknown")) return "";
  if (/\bgrowth\b/.test(s)) return "growth";
  if (/\bstarter\b/.test(s)) return "starter";
  if (/\bpro\b/.test(s)) return "pro";
  return planFromAmount(s);
}

function parseStripeDetail(raw: any): any | null {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

function subscriptionsFromStripeDetail(raw: any): any[] {
  const parsed = parseStripeDetail(raw);
  if (!parsed) return [];

  if (Array.isArray(parsed.subscriptions) && parsed.subscriptions.length > 0) {
    return parsed.subscriptions;
  }

  if (parsed.subscription && typeof parsed.subscription === "object") {
    const sub = parsed.subscription;
    const planDetails = parsed.plan_details || {};
    const amountCents = planDetails.amount || sub.plan?.amount || 0;
    return [
      {
        plan: planDetails.name || parsed.metadata?.planName || sub.plan?.nickname,
        subscription_status: sub.status,
        status: sub.status,
        amount_dollars: amountCents / 100,
        mrr: amountCents / 100,
      },
    ];
  }

  return [];
}

function isActiveSubscription(sub: any): boolean {
  const status = String(sub?.subscription_status || sub?.status || "").toLowerCase();
  const plan = String(sub?.plan || "").toLowerCase();
  return !status.includes("cancel") && plan !== "cancelled";
}

const WEBSITE_PRODUCT_IDS = new Set(["prod_VE6oOI9khC0x6p"]);

function normalizeProductLabel(value: unknown): string {
  return String(value || "").trim().toLowerCase();
}

/** Exact full Website product name (not “Website Demo”). */
export function isWebsiteProductName(value: unknown): boolean {
  return normalizeProductLabel(value) === "website";
}

function isWebsiteProductRef(value: unknown): boolean {
  const s = String(value || "").trim();
  return isWebsiteProductName(s) || WEBSITE_PRODUCT_IDS.has(s);
}

function activeStripeSubs(userData: any) {
  return subscriptionsFromStripeDetail(userData?.stripe_detail).filter(
    isActiveSubscription
  );
}

function stripeItemsOrPlanMatch(
  userData: any,
  match: (value: unknown) => boolean
): boolean {
  for (const sub of activeStripeSubs(userData)) {
    const items = Array.isArray(sub?.items) ? sub.items : [];
    if (
      items.some(
        (item: any) => match(item?.name) || match(item?.product_id)
      )
    ) {
      return true;
    }
    if (match(sub?.plan) || match(sub?.product_id)) return true;
  }
  return false;
}

/** Full Website Stripe item — can Publish and take over `/{username}`. */
export function hasWebsiteProduct(userData: any): boolean {
  return stripeItemsOrPlanMatch(userData, isWebsiteProductRef);
}

const BLOG_LIVE_PRODUCT_ID_SET = new Set([
  "prod_VWUVUK5OSiy2M3", // Blog 4
  "prod_VWUXrMdXBFaikH", // Blog 12
]);

function isBlogProductRef(value: unknown): boolean {
  const s = String(value || "").trim();
  if (!s) return false;
  if (BLOG_LIVE_PRODUCT_ID_SET.has(s)) return true;
  const lower = s.toLowerCase();
  if (lower === "blog_4" || lower === "blog_12") return true;
  if (lower.startsWith("prod_blog_") || lower.startsWith("prod_test_blog_")) {
    return true;
  }
  const token = lower.replace(/[_-]+/g, " ");
  return /\bblog\b/.test(token) && (/\b4\b/.test(token) || /\b12\b/.test(token));
}

/** Active Blog add-on on Stripe (`blog_4` / `blog_12`). */
export function hasBlogProduct(userData: any): boolean {
  return stripeItemsOrPlanMatch(userData, isBlogProductRef);
}

/**
 * Admin Engineering Controls flag (`user.website_demo`).
 * Unlocks Website editor + public `/website/:username` without flipping `/:username`.
 * Ignored when the creator has full Website (Stripe) / onboarding go-live —
 * see `resolveSubscriptionEntitlements` (`hasWebsiteDemo` is false then).
 */
export function hasWebsiteDemoFlag(userData: any): boolean {
  return Boolean(userData?.website_demo);
}

/** @deprecated Prefer hasWebsiteDemoFlag — same admin-flag check. */
export function hasWebsiteDemoProduct(userData: any): boolean {
  return hasWebsiteDemoFlag(userData);
}

export function hasActiveStripeSubscriptionRows(userData: any): boolean {
  return subscriptionsFromStripeDetail(userData?.stripe_detail).some(
    isActiveSubscription
  );
}

function planKeyFromSubscription(sub: any): PlanKey | "" {
  return (
    namedPlanKey(sub?.plan) ||
    planFromAmount(sub?.amount_dollars || sub?.mrr)
  );
}

/** Highest plan key among already-filtered active Stripe subs (no re-parse). */
function planKeyFromActiveSubs(subs: any[]): PlanKey | "" {
  let key: PlanKey | "" = "";
  for (const sub of subs) {
    const next = planKeyFromSubscription(sub);
    if (!next) continue;
    if (!key || TIER_RANK[next] > TIER_RANK[key]) key = next;
  }
  return key;
}

function stripeItemsMatchInSubs(
  subs: any[],
  match: (value: unknown) => boolean,
): boolean {
  for (const sub of subs) {
    const items = Array.isArray(sub?.items) ? sub.items : [];
    if (
      items.some(
        (item: any) => match(item?.name) || match(item?.product_id),
      )
    ) {
      return true;
    }
    if (match(sub?.plan) || match(sub?.product_id)) return true;
  }
  return false;
}

/**
 * Highest active Stripe plan only — ignores `subscription_type` /
 * `payment_plan` / `payment_status` columns.
 * Empty when there is no resolvable Stripe sub (demo creators, pre-webhook).
 *
 * **All entitlement / lock decisions must use this (or helpers below that
 * call it).** Do not gate Product Bank, affiliate, Growth, Embed, etc. on
 * user plan columns — those lag and disagree with live Stripe (demo Starter).
 */
export function resolveStripeOnlyPlanKey(userData: any): PlanKey | "" {
  return planKeyFromActiveSubs(activeStripeSubs(userData));
}

/**
 * Stripe-only plan snapshot for display + gating.
 * `key` is empty when there is no active Stripe plan (demo / pre-sync).
 * Does **not** fall back to `subscription_type` columns.
 */
export function resolveSubscriptionPlan(userData: any): {
  key: PlanKey | "";
  label: string;
  isHighestTier: boolean;
} {
  const key = resolveStripeOnlyPlanKey(userData);
  return {
    key,
    label: key ? PLAN_LABELS[key] : "",
    isHighestTier: key === "growth",
  };
}

/**
 * Product UX plan (Getting Started / website vs link-in-bio).
 * Stripe-only; defaults to Growth when there is no Stripe sub (onboarding +
 * demo creators). Never reads `subscription_type` columns.
 */
export function resolveProductPlanKey(userData?: any): PlanKey {
  return resolveStripeOnlyPlanKey(userData) || "growth";
}

/** True only when a raw string/value names Starter (checkout labels, etc.). */
export function isStarterPlanValue(value: unknown): boolean {
  return namedPlanKey(value) === "starter";
}

/**
 * Lowest-tier (Starter) locks — ads / affiliate / Product Bank.
 * Stripe-only: lock only when an active Stripe sub resolves to Starter.
 * No Stripe (demo / pre-sync) stays unlocked.
 */
export function isLowestTierLocked(userData: any): boolean {
  return resolveStripeOnlyPlanKey(userData) === "starter";
}

/**
 * Affiliate platforms (Amazon, ShopMy, LTK) + Product Bank are Pro+ on Stripe.
 * Lock only when Stripe says Starter; no Stripe sub → unlocked (demo OK).
 */
export function isAffiliateLocked(userData: any): boolean {
  return isLowestTierLocked(userData);
}

/**
 * Custom domain / Growth-only features. Stripe-only — no column fallback.
 */
export function isGrowthPlan(userData: any): boolean {
  return resolveStripeOnlyPlanKey(userData) === "growth";
}

export type CreatorProductSurface =
  | "link in bio"
  | "website"
  | "website demo";

export type SubscriptionEntitlements = {
  /** Stripe-only plan key; empty when unknown / no active sub. */
  key: PlanKey | "";
  /**
   * Product UX plan; never empty.
   * Stripe plan when present, otherwise Growth (onboarding / demo default).
   */
  productKey: PlanKey;
  label: string;
  isHighestTier: boolean;
  /** True only when Stripe says Starter. */
  isStarter: boolean;
  /** True only when Stripe says Pro. */
  isPro: boolean;
  /** True only when Stripe says Growth. */
  isGrowth: boolean;
  /** Starter lock for affiliates / ads (Stripe Starter only). */
  affiliateLocked: boolean;
  adsLocked: boolean;
  /** Product Bank + affiliate platforms — unlocked unless Stripe Starter. */
  hasAffiliate: boolean;
  /**
   * Dashboard Website editor UX.
   * True for full Website, Website Demo, or Growth onboarding with no Stripe yet.
   */
  hasPages: boolean;
  /** Full Website Stripe item only. */
  hasWebsiteProduct: boolean;
  /** Admin `website_demo` flag — false when full Website can Publish (flag ignored). */
  hasWebsiteDemo: boolean;
  /**
   * May Publish and take over public `/{username}`.
   * False for Demo-only (even if a leftover publish stamp exists).
   */
  canPublishWebsite: boolean;
  /** hasPages without go-live — demo editor + `/website/:user` only. */
  isWebsiteDemoOnly: boolean;
  product: CreatorProductSurface;
  Product: "Link in Bio" | "Website" | "Website Demo";
};

/**
 * Single entitlement snapshot for UI + gating.
 * Derive once per userData change; do not re-resolve ad hoc in children.
 *
 * Plan tier locks (Starter / Pro / Growth, Product Bank, affiliate, custom
 * domain, Embed Growth check) are **Stripe-only**. `subscription_type` /
 * `payment_plan` columns are never used for gating.
 *
 * Sidebar: Website nav is hasPages. Classic LiB nav stays for LiB-only plans
 * and Website Demo (staff-email onboard / admin flag); full Website hides LiB.
 * Admin / staff `website_demo` adds Website Demo Publish locks + dual LiB +
 * Website nav. Public `/website/:user` still serves when the flag is on
 * (opts omitted). A $97 Growth-only sub without Website/Demo is Link in Bio.
 * Public `/{username}` flips only with canPublishWebsite + Publish.
 */
export type ResolveEntitlementsOptions = {
  viewerIsAdmin?: boolean;
};

export function resolveSubscriptionEntitlements(
  userData?: any,
  opts?: ResolveEntitlementsOptions
): SubscriptionEntitlements {
  // One Stripe parse for plan + Website product + “has any active sub”.
  const activeSubs = activeStripeSubs(userData);
  const stripeKey = planKeyFromActiveSubs(activeSubs);
  const productKey: PlanKey = stripeKey || "growth";
  const affiliateLocked = stripeKey === "starter";
  const isGrowth = stripeKey === "growth";
  const hasWebsite = stripeItemsMatchInSubs(activeSubs, isWebsiteProductRef);
  const demoFlag = hasWebsiteDemoFlag(userData);
  // Creator dashboard: Demo flag must not unlock Website nav for normal creators.
  // Admin impersonation, public `/website/:user` (opts omitted), and internal
  // staff emails (auto-flagged on onboard) still honor the flag.
  const unlockDemoDashboard =
    opts?.viewerIsAdmin !== false ||
    isInternalStaffEmail(String(userData?.email || ""));
  const hasWebsiteDemo = demoFlag && !hasWebsite && unlockDemoDashboard;
  const onboardingWebsiteFallback =
    activeSubs.length === 0 && productKey === "growth";
  // Demo-only must not inherit Publish from onboarding Growth fallback.
  const canPublishWebsite =
    hasWebsite || (onboardingWebsiteFallback && !hasWebsiteDemo);
  const hasPages = canPublishWebsite || hasWebsiteDemo;
  const isWebsiteDemoOnly = hasWebsiteDemo;
  // Product Bank / affiliate: unlocked unless Stripe says Starter.
  const hasAffiliate = !affiliateLocked;

  let product: CreatorProductSurface = "link in bio";
  let Product: SubscriptionEntitlements["Product"] = "Link in Bio";
  if (isWebsiteDemoOnly) {
    product = "website demo";
    Product = "Website Demo";
  } else if (hasPages) {
    product = "website";
    Product = "Website";
  }

  return {
    key: stripeKey,
    productKey,
    label: stripeKey ? PLAN_LABELS[stripeKey] : "",
    isHighestTier: isGrowth,
    isStarter: stripeKey === "starter",
    isPro: stripeKey === "pro",
    isGrowth,
    affiliateLocked,
    adsLocked: affiliateLocked,
    hasAffiliate,
    hasPages,
    hasWebsiteProduct: hasWebsite,
    hasWebsiteDemo,
    canPublishWebsite,
    isWebsiteDemoOnly,
    product,
    Product,
  };
}

/**
 * Embed Page is a Growth entitlement (Stripe). Starter/Pro only keep it when
 * the `is_embed_search` flag is already on (legacy page in use).
 */
export function isEmbedPageEnabled(userData: any): boolean {
  if (resolveStripeOnlyPlanKey(userData) === "growth") return true;
  return Boolean(userData?.is_embed_search);
}

function centsOrDollars(amount: unknown): number {
  const n = Number(amount) || 0;
  // Stripe unit amounts for these plans are >= 1500 cents; 97 means $97.
  return n >= 1000 ? n / 100 : n;
}

/** Resolve Starter/Pro/Growth from a Stripe checkout session or similar payload. */
export function resolvePlanKeyFromCheckout(session: any): PlanKey | "" {
  return (
    namedPlanKey(session?.metadata?.planName) ||
    namedPlanKey(session?.planName) ||
    namedPlanKey(session?.plan_details?.name) ||
    planFromAmount(centsOrDollars(session?.plan_details?.amount)) ||
    planFromAmount(
      centsOrDollars(session?.line_items?.data?.[0]?.price?.unit_amount)
    ) ||
    planFromAmount(centsOrDollars(session?.amount_total)) ||
    planFromAmount(session?.amount) ||
    ""
  );
}
